// Maximus Bot (iGotSolutions): pending approvals, low-risk public reads, and status replies.

import { isApprovalAskBlock } from "./action-approval.js";

type AskLike = {
  kind?: string;
  status?: string;
  text?: string;
  approvalEffectId?: string;
  actions?: Array<{ id: string; label: string }>;
};

function asAsk(block: unknown): (AskLike & { kind: string }) | null {
  if (!block || typeof block !== "object") return null;
  const ask = block as AskLike;
  if (typeof ask.kind !== "string") return null;
  return ask as AskLike & { kind: string };
}

/** A public http(s) URL: no userinfo, and not an IP, localhost, or local suffix. */
export function isPublicHttpUrl(input: string | undefined): boolean {
  if (!input) return false;
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return false;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  if (url.username || url.password) return false;
  const host = url.hostname.replace(/\.$/, "").toLowerCase();
  if (!host || host === "localhost" || host.endsWith(".localhost") || host.endsWith(".local")) {
    return false;
  }
  if (host.includes(":") || host.split(".").every((label) => /^\d+$/.test(label))) return false;
  return true;
}

/**
 * Search and GET of a public page (including package registries) are low-risk reads.
 * Private hosts, credentials in the URL, and every other tool stay on the normal gate.
 */
export function isLowRiskPublicWebRead(toolName: string, url?: string): boolean {
  const name = toolName.toLowerCase();
  if (name === "web_search") return true;
  if (name === "web_fetch") return isPublicHttpUrl(url);
  return false;
}

export function messageNeedsApproval(blocks: unknown): boolean {
  if (!Array.isArray(blocks)) return false;
  return blocks.some((block) => {
    const ask = asAsk(block);
    return Boolean(ask && isApprovalAskBlock(ask) && ask.status !== "answered");
  });
}

export function messagesNeedApproval(messages: Array<{ blocks: unknown }>): boolean {
  return messages.some((message) => messageNeedsApproval(message.blocks));
}

export function pendingApprovalInMessages<T extends { blocks: unknown }>(
  messages: readonly T[],
): { message: T; block: AskLike & { text: string } } | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!message || !Array.isArray(message.blocks)) continue;
    for (let blockIndex = message.blocks.length - 1; blockIndex >= 0; blockIndex -= 1) {
      const ask = asAsk(message.blocks[blockIndex]);
      if (ask && isApprovalAskBlock(ask) && ask.status !== "answered") {
        return { message, block: { ...ask, text: typeof ask.text === "string" ? ask.text : "" } };
      }
    }
  }
  return null;
}

const STATUS_QUESTION =
  /^(?:(?:hey|hi|ok|okay|please|pls)[,!\s]+)?(?:is it done|are you (?:done|finished|still (?:working|going|busy))|what(?:'s| is|’s) (?:the )?status|status|progress|how long|how(?:'s| is|’s) it going|eta|any update|where are you|still working|done yet)[.!?]*$/i;

/** A short progress check. Longer notes that add work are not status questions. */
export function isRunStatusQuestion(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 80) return false;
  return STATUS_QUESTION.test(trimmed);
}

export function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return seconds === 1 ? "1 second" : `${seconds} seconds`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes === 1 ? "1 minute" : `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

export function runStatusReply(input: {
  pendingApproval: boolean;
  waitingTakeover?: boolean;
  step?: string | null;
  elapsedMs?: number | null;
}): string {
  const parts = [
    input.pendingApproval
      ? "Waiting for your approval."
      : input.waitingTakeover
        ? "Waiting for you."
        : "Still working.",
  ];
  const step = input.step?.trim();
  if (step) parts.push(`Current step: ${step}.`);
  if (typeof input.elapsedMs === "number" && Number.isFinite(input.elapsedMs)) {
    parts.push(`Elapsed: ${formatElapsed(input.elapsedMs)}.`);
  }
  return parts.join(" ");
}

const STATUS_STEER_NOTE =
  "The user is asking about progress. Answer with the current step and elapsed time, then continue the task. Do not start over and do not repeat an approval that is already pending. If this turn made an image, video, or PDF, inspect it before you say it is done: view the image, render video frames at several timestamps with ffmpeg (include 2.5s when the clip is at least 3 seconds), and check text overlap, cropping, and margins. Do not say it is done from memory of the edit.";

/** Model-facing steer text. The stored user message stays unchanged. */
export function steerTextForModel(text: string): string {
  if (!isRunStatusQuestion(text)) return text;
  return `${text.trim()}\n\n${STATUS_STEER_NOTE}`;
}

/** A later identical call reuses the card that is still waiting. */
export function shouldReusePendingApproval(input: {
  currentKey: string;
  baseKey: string;
  pendingStatus: string | null | undefined;
}): boolean {
  return input.currentKey !== input.baseKey && input.pendingStatus === "intended";
}
